/**
 * Fetches the placeholder image and converts it to a Blob
 * @returns {Promise<Blob>} A promise that resolves with the placeholder image Blob
 */
const getPlaceholderImageBlob = async (): Promise<Blob> => {
  try {
    const response = await fetch("/video-frame-placeholder.jpg");
    if (!response.ok) {
      throw new Error(`Failed to fetch placeholder image: ${response.statusText}`);
    }
    return await response.blob();
  } catch (error) {
    console.error("Failed to load placeholder image:", error);
    // Create a simple colored canvas as ultimate fallback
    const canvas = document.createElement("canvas");
    canvas.width = 320;
    canvas.height = 240;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#f0f0f0";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#666";
    ctx.font = "16px Arial";
    ctx.textAlign = "center";
    ctx.fillText("▶️", canvas.width / 2, canvas.height / 2);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.95);
    });
  }
};

/**
 * Custom function to extract a frame from a video Blob and return it as an image Blob.
 * Falls back to a placeholder image if extraction fails.
 * @param {Blob} videoBlob - The video blob from which to extract a frame.
 * @param {number} frameTime - The time position in the video (in seconds) from which to extract the frame.
 * @returns {Promise<Blob>} A promise that extracts the frame and resolves with the image Blob.
 */
export const extractFrameFromVideoBlob = async (videoBlob: Blob, frameTime = 0.5): Promise<Blob> => {
  try {
    if (!videoBlob) {
      throw new Error("No video blob provided");
    }

    return await new Promise<Blob>((resolve, reject) => {
      const videoUrl = URL.createObjectURL(videoBlob);
      const videoElement = document.createElement("video");

      videoElement.preload = "auto"; // Ensure video is preloaded
      videoElement.src = videoUrl;
      videoElement.playsInline = true; // Ensure video is not fullscreen
      videoElement.muted = true; // Mute the video to allow autoplay on iOS
      videoElement.setAttribute("playsinline", "");
      videoElement.setAttribute("webkit-playsinline", "");
      videoElement.load(); // Explicitly load the video

      const cleanup = () => {
        URL.revokeObjectURL(videoUrl);
      };

      const handleVideoError = (_e: ErrorEvent) => {
        cleanup();
        reject(new Error("Error loading video"));
      };

      // Set a timeout to prevent hanging indefinitely
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error("Video frame extraction timeout"));
      }, 10000); // 10 second timeout

      videoElement.addEventListener("loadedmetadata", () => {
        // Attempt to play the video to comply with autoplay policies.
        const playPromise = videoElement.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              videoElement.currentTime = frameTime;
              // Playback started successfully.
            })
            .catch((error) => {
              // Autoplay policy prevented playback; may need user gesture.
              console.error("Playback initiation failed:", error);
              cleanup();
              clearTimeout(timeout);
              reject(error);
            });
        }
      });

      videoElement.addEventListener("seeked", () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = videoElement.videoWidth || 320;
          canvas.height = videoElement.videoHeight || 240;

          const ctx = canvas.getContext("2d");
          if (!ctx) {
            throw new Error("Could not get canvas context");
          }

          ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

          canvas.toBlob(
            (imageBlob) => {
              cleanup();
              clearTimeout(timeout);
              if (imageBlob) {
                resolve(imageBlob);
              } else {
                reject(new Error("Failed to create image blob from canvas"));
              }
            },
            "image/jpeg",
            0.95,
          );
        } catch (error) {
          cleanup();
          clearTimeout(timeout);
          reject(error);
        }
      });

      videoElement.addEventListener("error", handleVideoError);
    });
  } catch (error) {
    console.error("Video frame extraction failed, using placeholder:", error);
    // Fallback to placeholder image
    return await getPlaceholderImageBlob();
  }
};
